import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  Mail,
  Globe,
} from 'lucide-react';
import { SortHeader, PlainHeader, type SortDir } from '@/components/list/sort-header';
import {
  FilterChip,
  FilterGroup,
  ClearFiltersButton,
} from '@/components/list/filter-chip';
import { formatRelative, formatShortDate } from '@/components/list/format-relative';
import { formatAdminDateTime } from '@/lib/format/admin-time';
import { adminFetch } from '@/api';

interface User {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  authProvider: string | null;
  createdAt: string;
  lastActiveAt: string | null;
  suspendedAt: string | null;
  deletedAt: string | null;
  _count: { memberships: number; expenses: number };
}

const PROVIDER_ICONS: Record<string, typeof Mail> = {
  email: Mail,
  google: Globe,
  apple: Shield,
};

type SortKey = 'name' | 'createdAt' | 'lastActive' | 'expenses';
type ProviderFilter = 'all' | 'email' | 'google' | 'apple';
type StatusFilter =
  | 'all'
  | 'verified'
  | 'unverified'
  | 'suspended'
  | 'deleted';

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('createdAt');
  const [dir, setDir] = useState<SortDir>('desc');
  const [provider, setProvider] = useState<ProviderFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const fetchUsers = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page),
      search,
      sort,
      dir,
    });
    if (provider !== 'all') params.set('provider', provider);
    if (status !== 'all') params.set('status', status);
    const res = await adminFetch(`/api/users?${params.toString()}`);
    const data = await res.json();
    setUsers(data.users);
    setTotal(data.total);
  }, [page, search, sort, dir, provider, status]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const totalPages = Math.ceil(total / 20);

  const applySort = (key: SortKey) => {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir(key === 'name' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const filtersActive =
    provider !== 'all' || status !== 'all' || search.length > 0;
  const clearFilters = () => {
    setProvider('all');
    setStatus('all');
    setSearch('');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-white tracking-tight">
            Users
          </h1>
          <p className="text-zinc-500 text-[13px] mt-0.5">
            {total.toLocaleString('en-IN')} registered users
          </p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
        <input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all"
        />
      </div>

      <div className="space-y-2">
        <FilterGroup label="Provider">
          {(['all', 'email', 'google', 'apple'] as const).map((v) => (
            <FilterChip
              key={v}
              label={v.charAt(0).toUpperCase() + v.slice(1)}
              active={provider === v}
              onClick={() => {
                setProvider(v);
                setPage(1);
              }}
            />
          ))}
        </FilterGroup>
        <FilterGroup label="Status">
          {(['all', 'verified', 'unverified', 'suspended', 'deleted'] as const).map(
            (v) => (
              <FilterChip
                key={v}
                label={v.charAt(0).toUpperCase() + v.slice(1)}
                active={status === v}
                onClick={() => {
                  setStatus(v);
                  setPage(1);
                }}
              />
            ),
          )}
          {filtersActive && <ClearFiltersButton onClick={clearFilters} />}
        </FilterGroup>
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="User"
                    sortKey="name"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <span className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">
                    Provider
                  </span>
                </th>
                <th className="px-5 py-3 text-left">
                  <span className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">
                    Groups
                  </span>
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Expenses"
                    sortKey="expenses"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Last Active"
                    sortKey="lastActive"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader
                    label="Signed Up"
                    sortKey="createdAt"
                    activeSort={sort}
                    activeDir={dir}
                    onSort={applySort}
                  />
                </th>
                <th className="px-5 py-3 text-left">
                  <span className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-10 text-center text-[12px] text-zinc-500"
                  >
                    No users match the current filters.
                  </td>
                </tr>
              )}
              {users.map((user) => {
                const ProviderIcon =
                  PROVIDER_ICONS[user.authProvider || 'email'] || Mail;
                const badge = user.deletedAt
                  ? { label: 'Deleted', tone: 'bg-red-500/10 text-red-400 border-red-500/20' }
                  : user.suspendedAt
                    ? { label: 'Suspended', tone: 'bg-amber-500/10 text-amber-400 border-amber-500/20' }
                    : user.emailVerified
                      ? { label: 'Verified', tone: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' }
                      : { label: 'Unverified', tone: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' };
                return (
                  <tr
                    key={user.id}
                    className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-5 py-3.5">
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center border border-white/[0.06]">
                          <span className="text-[11px] font-bold text-zinc-300">
                            {(user.name || user.email).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-[13px] text-white font-medium">
                            {user.name || '—'}
                          </p>
                          <p className="text-[11px] text-zinc-600">{user.email}</p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <ProviderIcon className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-[11px] text-zinc-500 capitalize">
                          {user.authProvider || 'email'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-[13px] text-zinc-400 font-[family-name:var(--font-jetbrains)]">
                        {user._count.memberships}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-[13px] text-zinc-400 font-[family-name:var(--font-jetbrains)]">
                        {user._count.expenses}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`text-[11px] font-[family-name:var(--font-jetbrains)] ${
                          user.lastActiveAt ? 'text-zinc-400' : 'text-zinc-700'
                        }`}
                        title={
                          user.lastActiveAt
                            ? formatAdminDateTime(user.lastActiveAt)
                            : undefined
                        }
                      >
                        {formatRelative(user.lastActiveAt)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-[11px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">
                        {formatShortDate(user.createdAt)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.tone}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${badge.tone.split(' ')[1]?.replace('text-', 'bg-')}`}
                        />
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-1.5">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-2 rounded-lg border border-white/[0.06] text-zinc-400 hover:bg-white/[0.04] disabled:opacity-30 transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 rounded-lg border border-white/[0.06] text-zinc-400 hover:bg-white/[0.04] disabled:opacity-30 transition-all"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
